import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Truck, Briefcase, FileText, ShoppingCart, Home, 
  HelpCircle, Edit, RefreshCw, Eye, Check, X, Clock, DollarSign,
  Trash2, Filter, Plus, ChevronRight, MapPin, Calendar, User, Phone,
  AlertTriangle, CheckCircle, XCircle, ArrowRight, Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const ServiceFormsTab = () => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: 'approved', notes: '', quoted_price: '' });

  useEffect(() => {
    fetchData();
  }, [statusFilter, typeFilter]);

  const fetchData = async () => {
    try {
      let params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (typeFilter) params.push(`form_type=${typeFilter}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const [submissionsRes, statsRes] = await Promise.all([
        api.get(`/forms/submissions${queryString}`),
        api.get('/forms/submissions/stats')
      ]);
      
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    
    setProcessing(true);
    try {
      const params = new URLSearchParams({
        status: reviewForm.status,
        ...(reviewForm.notes && { notes: reviewForm.notes }),
        ...(reviewForm.quoted_price && { quoted_price: reviewForm.quoted_price })
      });
      
      await api.put(`/forms/submissions/${reviewModal.id}/review?${params}`);
      toast.success(`Submission ${reviewForm.status}`);
      setReviewModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (submissionId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    setProcessing(true);
    try {
      await api.put(`/forms/submissions/${submissionId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Submission rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleArchive = async (submissionId) => {
    if (!window.confirm('Archive this submission?')) return;
    
    setProcessing(true);
    try {
      await api.delete(`/forms/submissions/${submissionId}`);
      toast.success('Submission archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive');
    } finally {
      setProcessing(false);
    }
  };

  const getFormIcon = (type) => {
    const icons = {
      local_pickup: <Package className="w-5 h-5 text-[#D4AF37]" />,
      local_delivery: <Truck className="w-5 h-5 text-[var(--success)]" />,
      business_courier: <Briefcase className="w-5 h-5 text-[#F4C2C2]" />,
      document_transport: <FileText className="w-5 h-5 text-amber-500" />,
      supply_run: <ShoppingCart className="w-5 h-5 text-cyan-500" />,
      home_support: <Home className="w-5 h-5 text-pink-500" />,
      general_assistance: <HelpCircle className="w-5 h-5 text-indigo-500" />,
      custom: <Edit className="w-5 h-5 text-[var(--text-muted)]" />
    };
    return icons[type] || <FileText className="w-5 h-5" />;
  };

  const getFormLabel = (type) => {
    const labels = {
      local_pickup: 'Local Pickup',
      local_delivery: 'Local Delivery',
      business_courier: 'Business Courier',
      document_transport: 'Document Transport',
      supply_run: 'Supply Run',
      home_support: 'Home Support',
      general_assistance: 'General Assistance',
      custom: 'Custom Request'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[var(--warning)]/15 text-yellow-700',
      reviewed: 'bg-[#D4AF37]/10 text-[#D4AF37]',
      approved: 'bg-[var(--success)]/15 text-green-700',
      converted: 'bg-[#F4C2C2]/15 text-purple-700',
      rejected: 'bg-[var(--error)]/15 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-[var(--background-subtle)]'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    if (urgency === 'urgent') {
      return <span className="px-2 py-0.5 bg-[var(--error)]/15 text-red-700 rounded-full text-xs">URGENT</span>;
    }
    if (urgency === 'priority') {
      return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">PRIORITY</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="forms-loading">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading service forms...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-forms-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <FileText className="w-6 h-6 text-[var(--primary)]" />
            Service Request Forms
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Review and manage customer service request submissions
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4 border-l-4 border-blue-400">
            <p className="text-3xl font-bold text-[#D4AF37]">{stats.total || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Total Submissions</p>
          </div>
          <div className="card p-4 border-l-4 border-yellow-400">
            <p className="text-3xl font-bold text-[var(--warning)]">{stats.pending || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Pending Review</p>
          </div>
          <div className="card p-4 border-l-4 border-green-400">
            <p className="text-3xl font-bold text-[var(--success)]">{stats.by_status?.approved || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Approved</p>
          </div>
          <div className="card p-4 border-l-4 border-purple-400">
            <p className="text-3xl font-bold text-[#F4C2C2]">{stats.by_status?.converted || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Converted</p>
          </div>
          <div className="card p-4 border-l-4 border-red-400">
            <p className="text-3xl font-bold text-[var(--error)]">{stats.by_status?.rejected || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Rejected</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex gap-1">
          <p className="text-sm font-medium mr-2 self-center">Status:</p>
          {['', 'pending', 'approved', 'converted', 'rejected'].map(s => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s === '' ? 'All' : s}
            </Button>
          ))}
        </div>
        <div className="flex gap-1 flex-wrap">
          <p className="text-sm font-medium mr-2 self-center">Type:</p>
          {['', 'local_pickup', 'local_delivery', 'supply_run', 'home_support', 'general_assistance'].map(t => (
            <Button
              key={t}
              variant={typeFilter === t ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter(t)}
            >
              {t === '' ? 'All' : getFormLabel(t)}
            </Button>
          ))}
        </div>
      </div>

      {/* Submissions List */}
      <div className="space-y-4">
        {submissions.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No Form Submissions</h3>
            <p className="text-[var(--text-muted)]">Customer service requests will appear here.</p>
          </div>
        ) : (
          submissions.map(sub => (
            <motion.div
              key={sub.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card p-5 ${sub.status === 'pending' ? 'border-2 border-yellow-300' : ''}`}
              data-testid={`form-${sub.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getFormIcon(sub.form_type)}
                    {getStatusBadge(sub.status)}
                    {getUrgencyBadge(sub.form_data?.urgency)}
                  </div>
                  
                  <h4 className="font-bold text-lg">{getFormLabel(sub.form_type)}</h4>
                  <p className="text-sm text-[var(--text-muted)]">
                    {sub.customer_name} • {sub.customer_phone}
                  </p>
                  
                  {/* Form-specific preview */}
                  <div className="mt-3 p-3 bg-[var(--accent)] rounded-lg text-sm">
                    {sub.form_type === 'supply_run' && (
                      <>
                        <p><strong>Store:</strong> {sub.form_data?.store_name}</p>
                        <p><strong>Budget:</strong> ${sub.form_data?.estimated_budget}</p>
                        <p><strong>Items:</strong> {sub.form_data?.shopping_list?.slice(0, 3).join(', ')}...</p>
                      </>
                    )}
                    {sub.form_type === 'local_pickup' && (
                      <>
                        <p><strong>Item:</strong> {sub.form_data?.item_description}</p>
                        <p><strong>From:</strong> {sub.form_data?.pickup_address?.substring(0, 50)}...</p>
                      </>
                    )}
                    {sub.form_type === 'home_support' && (
                      <>
                        <p><strong>Category:</strong> {sub.form_data?.service_category}</p>
                        <p><strong>Duration:</strong> {sub.form_data?.estimated_duration} hours</p>
                      </>
                    )}
                    {sub.form_type === 'social_visit' && (
                      <>
                        <p><strong>Visit Type:</strong> {sub.form_data?.visit_type}</p>
                        <p><strong>Client:</strong> {sub.form_data?.client_name}</p>
                      </>
                    )}
                    {sub.form_data?.special_instructions && (
                      <p className="mt-2 italic text-[var(--text-secondary)]">"{sub.form_data.special_instructions}"</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {sub.form_data?.preferred_date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(sub.created_at).toLocaleDateString()}
                    </span>
                    {sub.quoted_price && (
                      <span className="flex items-center gap-1 text-[var(--success)] font-medium">
                        <DollarSign className="w-3 h-3" />
                        ${sub.quoted_price}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedSubmission(sub)}>
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {sub.status === 'pending' && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => { setReviewModal(sub); setReviewForm({ status: 'approved', notes: '', quoted_price: '' }); }}
                        className="bg-[var(--success)] hover:bg-[var(--success)]/80 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" /> Review
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleReject(sub.id)}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleArchive(sub.id)}
                    className="text-[var(--error)]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* View Details Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                {getFormIcon(selectedSubmission.form_type)}
                {getFormLabel(selectedSubmission.form_type)} Details
              </h2>
              <Button variant="ghost" onClick={() => setSelectedSubmission(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedSubmission.status)}
                {selectedSubmission.quoted_price && (
                  <span className="text-lg font-bold text-[var(--success)]">${selectedSubmission.quoted_price}</span>
                )}
              </div>
              
              {/* Customer Info */}
              <div className="p-4 bg-[#D4AF37]/10 rounded-xl">
                <h4 className="font-bold text-[#D4AF37] mb-2">Customer Information</h4>
                <p><strong>Name:</strong> {selectedSubmission.customer_name}</p>
                <p><strong>Phone:</strong> {selectedSubmission.customer_phone}</p>
                {selectedSubmission.customer_email && (
                  <p><strong>Email:</strong> {selectedSubmission.customer_email}</p>
                )}
              </div>
              
              {/* Form Data */}
              <div className="p-4 bg-[var(--accent)] rounded-xl">
                <h4 className="font-bold mb-2">Request Details</h4>
                <div className="space-y-2 text-sm">
                  {Object.entries(selectedSubmission.form_data || {}).map(([key, value]) => {
                    if (key === 'form_type' || key === 'customer_id') return null;
                    if (value === null || value === undefined || value === '') return null;
                    
                    const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    const displayValue = Array.isArray(value) ? value.join(', ') : 
                                        typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                                        String(value);
                    
                    return (
                      <div key={key} className="flex justify-between">
                        <span className="text-[var(--text-muted)]">{label}:</span>
                        <span className="font-medium text-right max-w-[60%]">{displayValue}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Review Notes */}
              {selectedSubmission.review_notes && (
                <div className="p-4 bg-[var(--warning)]/10 rounded-xl">
                  <h4 className="font-bold text-amber-700 mb-2">Review Notes</h4>
                  <p>{selectedSubmission.review_notes}</p>
                </div>
              )}
              
              {/* Rejection Reason */}
              {selectedSubmission.rejection_reason && (
                <div className="p-4 bg-[var(--error)]/10 rounded-xl">
                  <h4 className="font-bold text-red-700 mb-2">Rejection Reason</h4>
                  <p>{selectedSubmission.rejection_reason}</p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setSelectedSubmission(null)} className="flex-1">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Review Submission</h2>
              <Button variant="ghost" onClick={() => setReviewModal(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <div className="flex gap-2">
                  <Button
                    variant={reviewForm.status === 'approved' ? 'default' : 'outline'}
                    onClick={() => setReviewForm({...reviewForm, status: 'approved'})}
                    className={reviewForm.status === 'approved' ? 'bg-[var(--success)]' : ''}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    variant={reviewForm.status === 'reviewed' ? 'default' : 'outline'}
                    onClick={() => setReviewForm({...reviewForm, status: 'reviewed'})}
                  >
                    <Eye className="w-4 h-4 mr-1" /> Mark Reviewed
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Quoted Price ($)</label>
                <Input
                  type="number"
                  value={reviewForm.quoted_price}
                  onChange={(e) => setReviewForm({...reviewForm, quoted_price: e.target.value})}
                  placeholder="Enter quoted price"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  value={reviewForm.notes}
                  onChange={(e) => setReviewForm({...reviewForm, notes: e.target.value})}
                  className="w-full p-3 border rounded-xl min-h-[100px] resize-none"
                  placeholder="Add review notes..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setReviewModal(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleReview} disabled={processing} className="flex-1 btn-primary">
                <Save className="w-4 h-4 mr-2" /> Save Review
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServiceFormsTab;
